import React from "react";
import classes from "./RentalEquipment.module.css";
import headerClasses from "../Home/Home.module.css";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  WhiteChairCovers,
  GreenChairAccents,
  PinkChairAccents,
  PinkTableRunner,
  GreenTableRunner,
  GreenTableRunner2,
  BlackSatinRunner,
  EmeraldCheeseclothRunner,
  BurgundyCheeseclothRunner,
  TerracottaCheeseclothRunner,
  Candle,
  PlasticGlasses,
  GreenVase,
  Cross,
  TableNumberCards,
  KingFisher,
  CollaredDove,
} from "../../Assets";

const items = [
  {
    key: "chairCovers",
    title: "White Chair Covers",
    images: [WhiteChairCovers],
    details: [
      { label: "Up to 130", price: "$227.50" },
      { label: "Up to 220", price: "$374" },
      { label: "A la carte", price: "$2.10/cover" },
    ],
  },
  {
    key: "chairAccents",
    title: "Chair Accents (green or pink)",
    images: [GreenChairAccents, PinkChairAccents],
    details: [
      { label: "Up to 130", price: "$130" },
      { label: "Up to 220", price: "$220" },
      { label: "A la carte", price: "$1.25/chair" },
    ],
  },
  {
    key: "tableRunners",
    title:
      "Table Runners (green cheesecloth, pink satin, black satin, emerald cheesecloth, burgundy cheesecloth, or terracotta cheesecloth)",
    images: [
      PinkTableRunner,
      GreenTableRunner,
      GreenTableRunner2,
      BlackSatinRunner,
      EmeraldCheeseclothRunner,
      BurgundyCheeseclothRunner,
      TerracottaCheeseclothRunner,
    ],
    details: [{ label: "Per table", price: "$4" }],
  },
  {
    key: "candles",
    title: "Flameless Candles and Garlands",
    images: [Candle],
    details: [
      { label: "Per table", price: "$5" },
      { label: "Per table with no candles", price: "$3" },
      { label: "Per table with no garlands", price: "$3" },
    ],
  },
  {
    key: "flutes",
    title: "Champagne Flutes / Drinkware (non‑glass)",
    images: [PlasticGlasses],
    details: [
      { label: "Up to 130", price: "$130" },
      { label: "Up to 220", price: "$220" },
    ],
  },
  {
    key: "vase",
    title: "Green Bud Vase (with or without faux flowers)",
    images: [GreenVase],
    details: [{ label: "Per table (up to 16 available)", price: "$10" }],
  },
  {
    key: "cross",
    title: "Decorative Wooden Cross",
    images: [Cross],
    details: [{ label: "Flat rate", price: "$75" }],
  },
  {
    key: "tableNumbers",
    title: "Table Numbers",
    images: [TableNumberCards],
    details: [{ label: "With package purchase", price: "$5" }],
  },
];

const packages = [
  {
    key: "collaredDove",
    title: "Collared Dove Package (up to 130 guests)",
    image: CollaredDove,
    bullets: [
      "Seat covers & accents",
      "Table runners",
      "Flameless candles",
      "Table numbers",
      "Candle garlands",
    ],
    prices: [
      { label: "Base price", price: "$500" },
      { label: "With champagne flutes", price: "$600" },
    ],
    footnote:
      "Pairs well with Pretty Bird’s Osprey package. Tablecloths not included. Setup is not included for Hummingbird or Blue Heron bookings; decorations will be set aside for your group to use as you please.",
  },
  {
    key: "kingfisher",
    title: "Kingfisher Package (up to 220 guests)",
    image: KingFisher,
    bullets: [
      "Seat covers & accents",
      "Table runners",
      "Flameless candles",
      "Table numbers",
      "Candle garlands",
    ],
    prices: [
      { label: "Base price", price: "$700" },
      { label: "With champagne flutes", price: "$850" },
    ],
    footnote:
      "Pairs well with Pretty Bird’s Silkie chicken package. Tablecloths not included. Setup is not included for Hummingbird or Blue Heron bookings; decorations will be set aside for your group to use as you please.",
  },
];

const RentalEquipment = () => {
  const [lightbox, setLightbox] = React.useState<{
    open: boolean;
    src: string;
    alt: string;
  }>({
    open: false,
    src: "",
    alt: "",
  });
  return (
    <div>
      {/* Header / Backdrop */}
      <div className={classes.headerBackdrop}>
        <div className={headerClasses.header}>
          <h1>Rentals & Add‑Ons</h1>
        </div>
      </div>

      {/* Intro / How it works */}
      <section className={classes.section}>
        <div className={classes.container}>
          <div className={classes.lede}>
            <p>
              From seat covers to table settings, our team handles delivery,
              setup, and teardown so you can focus on your day. Add‑ons pair
              best with Pretty Bird packages such as the Osprey, Silkie chicken,
              or Peacock.
            </p>
          </div>
        </div>
      </section>
      {/* Items & Pricing */}
      <section className={classes.section}>
        <div className={classes.container}>
          <h2 className={classes.h2}>Items & Pricing</h2>
          <div className={classes.grid}>
            {items.map((it) => (
              <article key={it.key} className={classes.card}>
                <div className={classes.mediaRow}>
                  {it.images.map((img, idx) => (
                    <button
                      type="button"
                      className={classes.thumbWrap}
                      key={idx}
                      onClick={() =>
                        setLightbox({ open: true, src: img, alt: it.title })
                      }
                      aria-label={"Expand " + it.title + " image"}
                    >
                      <img
                        src={img}
                        alt={it.title}
                        style={{
                          objectPosition: "top",
                        }}
                      />
                    </button>
                  ))}
                </div>
                <div className={classes.cardBody}>
                  <h3 className={classes.h3}>{it.title}</h3>
                  <ul className={classes.priceList}>
                    {it.details.map((d, i) => (
                      <li key={i}>
                        <span>{d.label}</span>
                        <strong>{d.price}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className={classes.section}>
        <div className={classes.container}>
          <h2 className={classes.h2}>Packages</h2>
          <div className={classes.grid}>
            {packages.map((p) => (
              <article key={p.key} className={classes.card}>
                <button
                  type="button"
                  className={classes.heroWrap}
                  onClick={() =>
                    setLightbox({ open: true, src: p.image, alt: p.title })
                  }
                  aria-label={"Expand " + p.title + " image"}
                >
                  <img src={p.image} alt={p.title} />
                </button>
                <div className={classes.cardBody}>
                  <h3 className={classes.h3}>{p.title}</h3>
                  <ul className={classes.bullets}>
                    {p.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                  <ul className={classes.priceList}>
                    {p.prices.map((d, i) => (
                      <li key={i}>
                        <span>{d.label}</span>
                        <strong>{d.price}</strong>
                      </li>
                    ))}
                  </ul>
                  <p className={classes.smallNote}>{p.footnote}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Dialog
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, src: "", alt: "" })}
        maxWidth="lg"
        fullWidth
      >
        <IconButton
          onClick={() => setLightbox({ open: false, src: "", alt: "" })}
          aria-label="Close image"
          sx={{ position: "absolute", top: 8, right: 8, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            style={{
              width: "100%",
              height: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              display: "block",
              background: "#1a3017",
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentalEquipment;
